<template>
  <div class="max-w-[580px] w-full px-7 pb-6 pt-5">
    <form id="pay-form">
      <fieldset class="flex flex-col gap-3 mb-8">
        <Label for="billingCycle">Billing cycle</Label>
        <div>
          <RadioGroupRoot
            v-model="selectedPriceId"
            class="grid grid-cols-2 gap-4"
            :default-value="selectedPriceId"
            aria-label="Billing cycle"
          >
            <label
              v-for="price in prices"
              :key="price.id"
              :for="price.stripe_price_id"
              class="flex items-center gap-3 border rounded-lg px-6 py-4 cursor-pointer transition duration-200 ease-in-out"
              :class="{
                'bg-primary-600 border-accent-400':
                  selectedPriceId === price.stripe_price_id,
                'bg-primary-700 border-primary-400':
                  selectedPriceId !== price.stripe_price_id,
              }"
            >
              <RadioGroupItem
                :id="price.stripe_price_id"
                class="hidden bg-neutral-900 size-[20px] rounded-full hover:bg-neutral-800 shadow-[inset_0_0_1px_rgba(0,0,0)] shadow-white focus:shadow-white outline-none"
                :value="price.stripe_price_id"
              >
                <RadioGroupIndicator
                  class="flex items-center justify-center w-full h-full relative after:content-[''] after:block after:size-[12px] after:rounded-[50%] after:bg-accent-400"
                />
              </RadioGroupItem>

              <span class="block font-semibold">
                <span class="block text-base text-neutral-300">
                  Pay
                  <span v-if="price.yearly_unit_amount">Yearly</span>
                  <span v-else>Monthly</span>
                </span>
                <span class="block text-sm text-neutral-500">
                  ${{ price.unit_amount / 100 }} / month / editor
                </span>
              </span>
            </label>
          </RadioGroupRoot>
        </div>
      </fieldset>
      <div v-if="prorationError">
        <p class="text-center">Something went wrong. Please try again.</p>
      </div>
      <template v-else>
        <div
          v-if="!isLoadingProration && !isRefetchingProration"
          class="bg-primary-600 p-5 rounded"
        >
          <div class="text-base leading-6 font-semibold mb-4">
            Order summary
          </div>
          <div class="space-y-4">
            <div v-for="l in prorationData.line_items" :key="l.description">
              <div class="flex justify-between text-xs font-semibold gap-4">
                <div>{{ l.description }}</div>
                <div>{{ centsToUnit({ cents: l.amount }) }}</div>
              </div>
            </div>
            <Button
              class="w-full"
              :disabled="isPending"
              :loading="isPending"
              @click="changePlanMutation"
            >
              Change plan
            </Button>
          </div>
          <p class="text-neutral-500 text-center mt-2">
            Any additional editors you add later will be prorated.
          </p>
        </div>
        <div v-else class="h-40 flex items-center justify-center">
          <Spinner />
        </div>
      </template>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from 'radix-vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';

import { centsToUnit } from '@/utils/currency.js';
import requests from '@vuex/requests.js';

import { Label } from '@ui/components/label';
import { Button } from '@ui/components/button';

import Spinner from '@ui/components/Spinner.vue';

const queryClient = useQueryClient();
const store = useStore();
const selectedPackage = ref();
const selectedPriceId = ref('');
const trxIntent = ref();
const props = defineProps({
  selectedProduct: {
    type: Object,
    default: null,
  },
});

const emits = defineEmits(['successPayment']);

const prices = { ...props.selectedProduct }.prices.sort(
  (a, b) => a.yearly_unit_amount - b.yearly_unit_amount
);
[selectedPackage.value] = prices;
selectedPriceId.value = prices[0].stripe_price_id;

const {
  isLoading: isLoadingProration,
  data: prorationData,
  refetch,
  isRefetching: isRefetchingProration,
  error: prorationError,
} = useQuery({
  queryKey: ['getProration'],
  queryFn: () =>
    requests.workspaces.subscription.proration(
      store.getters['workspace/getId'],
      {
        priceId: selectedPriceId.value,
      }
    ),
});

watch(selectedPriceId, async next => {
  [selectedPackage.value] = prices.filter(
    price => price.stripe_price_id === next
  );

  await refetch();
});

onMounted(async () => {
  try {
    const intentRes = await requests.workspaces.subscription.getIntent(
      store.getters['workspace/getId']
    );
    trxIntent.value = intentRes.intent;
  } catch (error) {
    console.log(error);
  }
});

const { isPending, mutate: changePlanMutation } = useMutation({
  mutationFn: async () => {
    const res = await requests.workspaces.subscription.changePlan(
      store.getters['workspace/getId'],
      {
        priceId: selectedPriceId.value,
      }
    );
    return res;
  },
  onSuccess: async res => {
    emits('successPayment');
    store.commit('workspace/setWorkspace', { value: res });
    await queryClient.invalidateQueries();
    toast.success('Your subscription has been successfully changed');
  },
  onError: async () => {
    toast.error('Something went wrong, please try again later');
  },
});
</script>
